/**
 * Suche und Filter für Rezepte.
 *
 * Die Suche läuft im Gerät über MiniSearch. Damit funktioniert sie auch ohne
 * Netz, zum Beispiel im Supermarkt, und reagiert ohne Verzögerung. Die
 * Filter sind reine Funktionen und lassen sich dadurch einzeln testen.
 */
import MiniSearch from 'minisearch';
import type { RecipeSummary } from '../api/types.ts';

/** Normalisiert Text für den Vergleich: Kleinschreibung, Umlaute aufgelöst. */
export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .replaceAll('ß', 'ss')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Ein Eintrag im Suchindex. */
interface SearchDocument {
  id: string;
  title: string;
  description: string;
  tags: string;
  /** Wird erst beim Filtern nach Zutaten gebraucht und nachgereicht. */
  ingredients: string;
}

/** Erzeugt den Suchindex aus den Rezepten. */
export function buildSearchIndex(recipes: readonly RecipeSummary[]): MiniSearch<SearchDocument> {
  const index = new MiniSearch<SearchDocument>({
    fields: ['title', 'description', 'tags', 'ingredients'],
    storeFields: ['id'],
    searchOptions: {
      prefix: true,
      fuzzy: (term) => (term.length >= 5 ? 0.2 : false),
      boost: { title: 3, tags: 2, ingredients: 2, description: 1 },
    },
  });

  index.addAll(
    recipes.map((recipe) => ({
      id: recipe.id,
      title: recipe.title,
      description: recipe.description ?? '',
      tags: recipe.tagNames.join(' '),
      ingredients: '',
    })),
  );

  return index;
}

/** Filterzustand. */
export interface RecipeFilter {
  /** Suchtext. */
  readonly query: string;
  /** Kategorien (mindestens eine muss passen). */
  readonly categoryIds: readonly string[];
  /** Schlagwörter. */
  readonly tagNames: readonly string[];
  /** Sollen alle Schlagwörter passen? */
  readonly tagsAll: boolean;
  /** Schwierigkeitsgrade. */
  readonly difficulties: readonly ('easy' | 'medium' | 'hard')[];
  /** Höchstens Gesamtzeit in Minuten, 0 bedeutet ohne Grenze. */
  readonly maxTotalMinutes: number;
  /** Höchstens aktive Zeit in Minuten, 0 bedeutet ohne Grenze. */
  readonly maxActiveMinutes: number;
  /** Mindestens Bewertung von 1 bis 5, 0 bedeutet ohne Grenze. */
  readonly minRating: number;
  /** Soll die eigene Bewertung statt des Durchschnitts gelten? */
  readonly ratingByMe: boolean;
  /** Nur Rezepte ohne Bewertung. */
  readonly unratedOnly: boolean;
  /** Nur vorgemerkte Rezepte. */
  readonly favoritesOnly: boolean;
  /** Nur Rezepte, die vor diesem Zeitpunkt gekocht wurden oder noch nie. */
  readonly cookedBefore: string | null;
  /** Zutaten, die vorkommen müssen. */
  readonly withIngredients: readonly string[];
  /** Zutaten, die nicht vorkommen dürfen. */
  readonly withoutIngredients: readonly string[];
  /** Sortierung. */
  readonly sort: SortKey;
}

/** Mögliche Sortierungen. */
export type SortKey = 'relevance' | 'rating' | 'totalTime' | 'activeTime' | 'newest' | 'lastCooked' | 'title';

/** Ein leerer Filter. */
export const EMPTY_FILTER: RecipeFilter = {
  query: '',
  categoryIds: [],
  tagNames: [],
  tagsAll: false,
  difficulties: [],
  maxTotalMinutes: 0,
  maxActiveMinutes: 0,
  minRating: 0,
  ratingByMe: false,
  unratedOnly: false,
  favoritesOnly: false,
  cookedBefore: null,
  withIngredients: [],
  withoutIngredients: [],
  sort: 'newest',
};

/** Ist irgendein Filter gesetzt? */
export function hasActiveFilters(filter: RecipeFilter): boolean {
  return (
    filter.categoryIds.length > 0 ||
    filter.tagNames.length > 0 ||
    filter.difficulties.length > 0 ||
    filter.maxTotalMinutes > 0 ||
    filter.maxActiveMinutes > 0 ||
    filter.minRating > 0 ||
    filter.unratedOnly ||
    filter.favoritesOnly ||
    filter.cookedBefore !== null ||
    filter.withIngredients.length > 0 ||
    filter.withoutIngredients.length > 0
  );
}
